import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { ToastrService } from 'ngx-toastr';
import { GenericService } from 'src/app/services';
import { HelperService } from 'src/app/services/helper.service';

/** The status a file must be sitting at before any of these buttons exist. */
export const RESET_ALLOWED_STATUS = 'Deal Fell Through';

/** Section key -> what the button and the confirmation call it. Mirrors RESET_SECTIONS on the server. */
const SECTION_LABELS: { [key: string]: string; } = {
	appraisal: 'Appraisal',
	title: 'Title Commitment',
	flood: 'Flood Certificate',
	pmi: 'MI Rate Quote',
};

/**
 * ADMIN-ONLY RESET, SHOWN ONLY ON A FILE MARKED 'Deal Fell Through'.
 *
 * Two shapes:
 *
 *   * "Reset <this section>" — one button per Order panel (Appraisal, Title, Flood, PMI).
 *     Deletes only that section's orders and puts only its own flag back to Pending.
 *   * "Reset All API's"      — beside the status dropdown in the navbar. All four at once.
 *
 * Credit and the CAP score are never in scope: those follow the borrower, not the property.
 *
 * Both buttons hit the same endpoint; the difference is the `sections` array. The server
 * re-checks the admin role AND the status, so hiding these is presentation, not protection.
 */
@Component({
	selector: 'app-reset-file-apis',
	templateUrl: './reset-file-apis.component.html',
	styleUrls: ['./reset-file-apis.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ButtonsModule,
		DialogsModule
	]
})
export class ResetFileApisComponent
{

	@Input() public loanApplicationId: any;
	@Input() public isStatusClosed: any;

	/**
	 * Which button this instance renders:
	 *   'appraisal' | 'title' | 'flood' | 'pmi' — the per-section button, mounted on that panel
	 *                                             and clearing only that section.
	 *   'all'                                   — the "Reset All API's" button beside the status
	 *                                             dropdown in the navbar; clears all four.
	 */
	@Input() public section: string = '';

	@Output() private updateLoader = new EventEmitter;

	/** Non-empty while a confirmation is open — holds the sections that Reset would send. */
	public pendingSections: string[] = [];
	public resetting = false;

	constructor (
		private genericService: GenericService,
		private toaster: ToastrService,
		public helperService: HelperService,
	)
	{
	}

	/** Is this the navbar's all-sections button rather than a per-panel one? */
	public get isAllButton (): boolean
	{
		return this.section === 'all';
	}

	/** This panel's own label, e.g. 'Flood Certificate'. Empty for the all-three button. */
	public get sectionLabel (): string
	{
		return SECTION_LABELS[this.section] || '';
	}

	/**
	 * Both guards the server also applies. Role comes off the JWT; status is kept current by
	 * the navbar, which re-reads it after every accepted status change.
	 */
	public get canReset (): boolean
	{
		return this.helperService.userInfo?.role_id === 'ADMIN'
			&& this.helperService.activeLoanApplicationStatus === RESET_ALLOWED_STATUS;
	}

	/** True when the open confirmation covers every section. Drives the extra address warning. */
	public get isFullReset (): boolean
	{
		return this.pendingSections.length === Object.keys(SECTION_LABELS).length;
	}

	/** Human list for the confirmation body. */
	public get pendingLabels (): string[]
	{
		return this.pendingSections.map((key) => SECTION_LABELS[key] || key);
	}

	/** The one button this instance owns — its section, or all of them in the navbar. */
	public confirm ()
	{
		this.openConfirm(this.isAllButton ? Object.keys(SECTION_LABELS) : [this.section]);
	}

	private openConfirm (sections: string[])
	{
		if (!this.loanApplicationId || this.resetting || !this.canReset)
		{
			return;
		}
		this.pendingSections = sections;
	}

	public closeConfirm ()
	{
		this.pendingSections = [];
	}

	public confirmReset ()
	{
		const sections = this.pendingSections;
		this.pendingSections = [];

		if (!sections.length)
		{
			return;
		}

		this.resetting = true;
		this.updateLoader.emit(true);

		try
		{
			this.genericService.post('reset-file-apis', {
				loanapplication_id: this.loanApplicationId,
				sections
			}).subscribe({
				next: (response) =>
				{
					if (response.success)
					{
						const labels = (response.data?.resetApis || sections).join(', ');
						this.toaster.success(response.data?.addressReset
							? `${labels} reset. The property address is back to TBD.`
							: `${labels} reset.`);

						// Broadcast rather than emit upward: the all-sections button lives in the
						// navbar, nowhere near the panels that have to redraw. The right side bar
						// subscribes and rebuilds exactly the sections named here.
						this.helperService.onFileApisReset.next(response.data?.sections || sections);
					}
					else
					{
						this.toaster.error(response.message || 'The reset could not be completed.');
					}
					this.resetting = false;
					this.updateLoader.emit(false);
				},
				error: (err) =>
				{
					console.error(err);
					this.toaster.error(err?.error?.message || 'The reset could not be completed.');
					this.resetting = false;
					this.updateLoader.emit(false);
				}
			});
		} catch (error)
		{
			console.error(error);
			this.resetting = false;
			this.updateLoader.emit(false);
		}
	}
}
